import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, CreditCard, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";

const MAX_ATTEMPTS = 6;
const POLL_MS = 2000;

export default function BookingSuccessPage() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const sessionId = params.get("session_id");
  const isFree = params.get("free") === "1";

  const [status, setStatus] = useState(sessionId ? "polling" : "ok");
  const [detail, setDetail] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) { setStatus("timeout"); return; }
        setTimeout(poll, POLL_MS);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="container-fl py-20 max-w-lg text-center" data-testid="booking-success-page">
      {status === "polling" && (
        <>
          <Loader2 size={40} className="mx-auto animate-spin text-emerald-600 mb-4"/>
          <h1 className="text-3xl font-black tracking-tighter mb-2">Confirming your payment…</h1>
          <p className="text-neutral-600">Please don't close this page.</p>
        </>
      )}

      {(status === "ok" || status === "paid") && (
        <>
          <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-4">
            <CheckCircle2 size={32} className="text-emerald-600"/>
          </div>
          <h1 className="text-3xl font-black tracking-tighter mb-2">
            {status === "paid" ? "Payment successful!" : isFree ? "Discovery call booked!" : "Booking requested!"}
          </h1>
          <p className="text-neutral-600 mb-2">
            {status === "paid"
              ? "Your session is confirmed and paid. See you in the gym!"
              : isFree
                ? "Your free 15-min discovery call is booked — no card required."
                : "Your trainer will accept your booking shortly. Once accepted, you can pay from your dashboard."}
          </p>
          {bookingId && <p className="text-xs text-neutral-400 mb-6">Booking ref: {bookingId}</p>}
          {detail && (
            <div className="text-xs text-neutral-500 mb-4">Amount: €{(detail.amount_total || 0) / 100}</div>
          )}
        </>
      )}

      {(status === "timeout" || status === "error") && (
        <>
          <Loader2 size={40} className="mx-auto text-amber-500 mb-4"/>
          <h1 className="text-2xl font-black tracking-tighter mb-2">Still processing…</h1>
          <p className="text-neutral-600 mb-4">Your payment is being processed. Check your dashboard in a minute.</p>
        </>
      )}

      {status === "expired" && (
        <>
          <XCircle size={40} className="mx-auto text-red-500 mb-4"/>
          <h1 className="text-2xl font-black tracking-tighter mb-2">Payment session expired</h1>
          <p className="text-neutral-600 mb-4">No charge was made. You can retry from your dashboard.</p>
        </>
      )}

      <div className="flex gap-2 justify-center mt-6">
        <Link to="/dashboard" className="btn-primary" data-testid="go-dashboard">
          <CreditCard size={14}/> Go to dashboard <ChevronRight size={14}/>
        </Link>
      </div>
    </div>
  );
}
